import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { LocalStorageKeys } from "@/common/constants";

type Theme = "light" | "dark";
type ThemeScope = "patient" | "staff" | "public";

const THEME_KEYS: Record<ThemeScope, string> = {
  patient: "care_theme_patient",
  staff: "care_theme_staff",
  public: "care_theme_public",
};
const THEME_SCOPE_EVENT = "care-theme-scope-change";

const getThemeScope = (): ThemeScope => {
  if (localStorage.getItem(LocalStorageKeys.patientTokenKey)) return "patient";
  if (
    localStorage.getItem(LocalStorageKeys.accessToken) ||
    localStorage.getItem(LocalStorageKeys.refreshToken)
  ) {
    return "staff";
  }
  return "public";
};

const getStoredTheme = (scope: ThemeScope): Theme =>
  localStorage.getItem(THEME_KEYS[scope]) === "dark" ? "dark" : "light";

export const notifyThemeScopeChange = () => {
  window.dispatchEvent(new Event(THEME_SCOPE_EVENT));
};

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [scope, setScope] = useState<ThemeScope>(getThemeScope);
  const [theme, setThemeState] = useState<Theme>(() =>
    getStoredTheme(getThemeScope()),
  );

  const refreshThemeScope = useCallback(() => {
    const nextScope = getThemeScope();
    setScope(nextScope);
    setThemeState(getStoredTheme(nextScope));
  }, []);

  useEffect(() => {
    window.addEventListener(THEME_SCOPE_EVENT, refreshThemeScope);
    return () =>
      window.removeEventListener(THEME_SCOPE_EVENT, refreshThemeScope);
  }, [refreshThemeScope]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  const setTheme = useCallback(
    (nextTheme: Theme) => {
      localStorage.setItem(THEME_KEYS[scope], nextTheme);
      setThemeState(nextTheme);
    },
    [scope],
  );

  const value = useMemo(
    () => ({ theme, resolvedTheme: theme, setTheme }),
    [setTheme, theme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
